import widgetService from '@/services/widgetService';
import { getNextAvailableColor } from '@/utils/calendarColors';
import { v4 as uuidv4 } from 'uuid';

const state = {
    events: [],
    loading: false,
    error: null,
    widgetId: null,
};

const CALENDAR_WIDGET_NAME = 'widget-calendar-events';

const findCalendarEventsWidget = async (widgetService) => {
    try {
        const allWidgets = await widgetService.getAllWidgets();
        return allWidgets.find((widget) => widget.name === CALENDAR_WIDGET_NAME);
    } catch (error) {
        console.warn('Failed to fetch widgets:', error);
        return null;
    }
};

const mutations = {
    SET_LOADING(state, loading) {
        state.loading = loading;
    },

    SET_ERROR(state, error) {
        state.error = error;
    },

    SET_EVENTS(state, { events, widgetId }) {
        state.events = events;
        if (widgetId) {
            state.widgetId = widgetId;
        }
    },

    ADD_EVENT(state, event) {
        state.events.push(event);
    },

    UPDATE_EVENT(state, { id, eventData }) {
        const index = state.events.findIndex((e) => e.id === id);
        if (index !== -1) {
            state.events = state.events.map((event) =>
                event.id === id ? { ...event, ...eventData } : event,
            );
        }
    },

    DELETE_EVENT(state, eventId) {
        state.events = state.events.filter((e) => e.id !== eventId);
    },
};

const actions = {
    async loadCalendarEvents({ commit }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        try {
            const widgetType = 'calendar-events';
            const defaultWidget = {
                name: CALENDAR_WIDGET_NAME,
                type: widgetType,
                state: {
                    events: [],
                },
            };

            let widget = await findCalendarEventsWidget(widgetService);

            if (!widget) {
                widget = await widgetService.createWidget(defaultWidget);
            }

            const events = widget.state.events || [];
            commit('SET_EVENTS', { events, widgetId: widget.uuid });

            return events;
        } catch (error) {
            commit('SET_ERROR', error);
            console.error('Failed to load calendar events:', error);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async createCalendarEvent({ commit, state }, eventData) {
        try {
            let color = eventData.color;
            if (!color) {
                const eventDate = new Date(eventData.startDate);
                const eventDay = eventDate.toDateString();

                const eventsForDay = state.events.filter((event) => {
                    const eventStart = new Date(event.startDate);
                    return eventStart.toDateString() === eventDay;
                });

                const availableColor = getNextAvailableColor(eventsForDay);
                color = availableColor.id;
            }

            const newEvent = {
                id: uuidv4(),
                ...eventData,
                color: color,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };

            commit('ADD_EVENT', newEvent);

            await syncEventsToWidget(state.events, state);

            return newEvent;
        } catch (error) {
            commit('DELETE_EVENT', newEvent.id);
            console.error('Failed to create calendar event:', error);
            throw error;
        }
    },

    async updateCalendarEvent({ commit, state }, { id, eventData }) {
        try {
            const originalEvent = state.events.find((e) => e.id === id);
            if (!originalEvent) {
                throw new Error(`Event with id ${id} not found`);
            }

            const updatedEvent = {
                ...originalEvent,
                ...eventData,
                updatedAt: new Date().toISOString(),
            };

            commit('UPDATE_EVENT', { id, eventData: updatedEvent });

            await syncEventsToWidget(state.events, state);

            return updatedEvent;
        } catch (error) {
            commit('UPDATE_EVENT', { id, eventData: originalEvent });
            console.error('Failed to update calendar event:', error);
            throw error;
        }
    },

    async deleteCalendarEvent({ commit, state }, eventId) {
        try {
            const eventToDelete = state.events.find((e) => e.id === eventId);
            if (!eventToDelete) {
                throw new Error(`Event with id ${eventId} not found`);
            }

            commit('DELETE_EVENT', eventId);

            await syncEventsToWidget(state.events, state);

            return eventId;
        } catch (error) {
            commit('ADD_EVENT', eventToDelete);
            console.error('Failed to delete calendar event:', error);
            throw error;
        }
    },
};

const syncEventsToWidget = async (events, state) => {
    const widget = await findCalendarEventsWidget(widgetService);

    if (!widget) {
        throw new Error('Calendar events widget not found');
    }

    try {
        const widgetData = {
            state: {
                events: events,
                lastSync: new Date().toISOString(),
            },
        };
        await widgetService.updateWidget(widget.uuid, widgetData);
    } catch (error) {
        console.warn('Failed to sync calendar events to widget:', error);
        throw error;
    }
};

const getters = {
    getEvents: (state) => state.events,

    getEventById: (state) => (id) => {
        return state.events.find((event) => event.id === id) || null;
    },

    isLoading: (state) => state.loading,

    getError: (state) => state.error,

    getEventsByDateRange: (state) => (startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);

        return state.events.filter((event) => {
            const eventDate = new Date(event.startDate);
            return eventDate >= start && eventDate <= end;
        });
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
